import { createSelector } from 'reselect';
import sortByProp, { sortByProps } from '../lib/sortByProp.js';
import { matchPath } from 'react-router';

const stableObject = {};
const stableArray = [];

const userServiceProviderIds = (state) => (state.user || stableObject).serviceProviderIds;
const selectRouterLocationPathname = (state) => state.router.location.pathname;

export const serviceProviders = (state) => (state.app || stableObject).serviceProviders;
export const serviceProvider = (state) => (state || {}).currentServiceProvider;
export const serviceProviderId = createSelector(serviceProvider, (serviceProvider) => serviceProvider.id);
export const selectServiceProviderFromURL = createSelector(selectRouterLocationPathname, (selectRouterLocationPathname) => {
  const match = matchPath(selectRouterLocationPathname, { path: '/:serviceProviderId' });
  return match.params.serviceProviderId;
});

export const serviceProviderIdWithFallback = createSelector(serviceProvider, userServiceProviderIds, (serviceProvider, serviceProviderIds) => {
  return serviceProvider.id || (serviceProviderIds || stableArray)[0];
});
export const serviceProviderName = createSelector(serviceProvider, (serviceProvider) => serviceProvider.name);
export const serviceProviderPermissions = createSelector(serviceProvider, (serviceProvider) => serviceProvider.permissions);
export const serviceProviderUsersRaw = createSelector(serviceProvider, (serviceProvider) => serviceProvider.users);
export const serviceProviderUsers = createSelector(serviceProviderUsersRaw, (users) => {
  return users
    .filter((n) => {
      const active = n.active !== '0';
      const tresioUser = n.tresioUser === '1';
      const multiUser = n.isMultiUser === '1';
      return active && (!multiUser || tresioUser);
    })
    .map((n) => ({
      ...n,
      fullName: `${n.firstName || ''} ${n.lastName || ''}`,
    }));
});
export const serviceProviderRolesRaw = createSelector(serviceProvider, (serviceProvider) => serviceProvider.roles);
export const serviceProviderRoles = createSelector(serviceProviderRolesRaw, serviceProviderUsers, (roles, users) => {
  return roles.map((n) => ({
    ...n,
    users: users.filter((o) => (o.roles || []).includes(parseFloat(n.id))).map((n) => n.id),
  }));
});
export const serviceProviderAdditionalFields = createSelector(serviceProvider, (serviceProvider) => serviceProvider.additionalFields);
export const serviceProviderTemplates = createSelector(serviceProvider, (serviceProvider) => (serviceProvider || {}).templates || stableArray);
export const serviceProviderTextTemplates = createSelector(serviceProviderTemplates, (templates) => templates.filter((n) => n.type === 'text'));
export const serviceProviderEmailTemplates = createSelector(serviceProviderTemplates, (templates) => templates.filter((n) => n.type === 'email'));

export const serviceProviderEmails = createSelector(serviceProvider, (serviceProvider) => serviceProvider.emails);
export const serviceProviderStats = createSelector(serviceProvider, (serviceProvider) => serviceProvider.stats);

export const serviceProviderLocations = createSelector(serviceProvider, (serviceProvider) => serviceProvider.locations);
export const serviceProviderServicePersons = createSelector(serviceProvider, (serviceProvider) => (serviceProvider.servicePersons || stableArray).filter((n) => n.active !== '0'));
export const serviceProviderNotifications = createSelector(serviceProvider, (serviceProvider) => serviceProvider.notifications);
export const serviceProviderPipelines = createSelector(serviceProvider, (serviceProvider) => serviceProvider.pipelines);
export const serviceProviderServices = createSelector(serviceProvider, (serviceProvider) => serviceProvider.services);
export const serviceProviderPlatformRegistrations = createSelector(serviceProvider, (serviceProvider) => serviceProvider.platformRegistrations);
const serviceProviderTrackedPhoneNumbersRaw = createSelector(serviceProvider, (serviceProvider) => serviceProvider.trackedPhoneNumbers || stableArray);
export const trackedPhoneNumbers = createSelector(serviceProviderTrackedPhoneNumbersRaw, serviceProviderLocations, serviceProviderPlatformRegistrations, (numbers, locations, registrations) =>
  numbers
    .map((n) => ({
      ...n,
      serviceLocationName: (locations.find((loc) => loc.id === n.serviceLocationId) || stableObject).service_location_name || n.serviceLocationName,
      platformCode: (registrations.find((o) => o.id === n.platformRegistrationId) || {}).platform_code,
    }))
    .sort(sortByProps(['platformRegistrationId', 'phoneNumber']))
);
export const serviceProviderDestinationPhoneNumbersRaw = createSelector(serviceProvider, (serviceProvider) => serviceProvider.destinationPhoneNumbers);
export const destinationPhoneNumbers = createSelector(serviceProviderDestinationPhoneNumbersRaw, serviceProviderName, serviceProviderUsers, serviceProviderLocations, (numbers, serviceProviderName, users, locations) =>
  numbers
    .map((n) => {
      const user = users.find((o) => o.id === n.userId) || {};
      const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const location = (locations.find((o) => o.id === n.serviceLocationId) || {}).service_location_name;
      return {
        ...n,
        cookedLabel: n.label || userName || location || serviceProviderName,
      };
    })
    .sort(sortByProp('phoneNumber'))
);
export const utmSources = createSelector(serviceProvider, (serviceProvider) => serviceProvider.utmSources);
export const utmCampaigns = createSelector(serviceProvider, (serviceProvider) => serviceProvider.utmCampaigns);

export const defaultServiceLocationSelector = createSelector(serviceProviderLocations, (locations) => {
  const locationsData = locations || stableArray;
  return locationsData[0] || stableObject;
});

export const officeHours = createSelector(serviceProvider, (serviceProvider) => {
  const officeHoursData = serviceProvider.officeHours || [];
  return officeHoursData;
});
